'use client';

import React from 'react';

import classNames from 'classnames';

import {Button} from '@udemy-v2/react-core-components';
import {Icon} from '@udemy-v2/icons';
import {formatIndianCompactNumber, useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {TrackImpression, useTracker} from '@udemy-v2/event-tracking';
import {OnePaneLayoutFullWidthSection} from '@udemy-v2/react-layout-components';

import {Heading, SubHeading} from '@/lib/heading-level';
import {useTrendingSkills} from '../../data/hooks/use-trending-skills';
import type {
    CategoryTopics,
    EnrollmentStats,
    TrendingSkillsVariant,
    TrendingTopic,
} from '../../data/types';
import {
    createClickEvent,
    createDiscoveryImpressionEvent,
    createDiscoveryItemClickEvent,
    createTrendingSkillsDiscoveryTrackingData,
} from '../../data/tracking';
import {TrendingSkillsSkeleton} from './trending-skills-skeleton';
import styles from './trending-skills.module.scss';

const TOPIC_LIMIT = 3;

interface TrendingSkillsProps {
    variant?: TrendingSkillsVariant;
}

interface TopicRowProps {
    topic: TrendingTopic;
    position: number;
    enrollmentCount?: number;
    variant: TrendingSkillsVariant;
}

const TopicRow = ({topic, position, enrollmentCount, variant}: TopicRowProps) => {
    const {ninterpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const tracker = useTracker();
    const discoveryEvent = createTrendingSkillsDiscoveryTrackingData(topic, position);
    const formatStatValue = (statValue: number) =>
        variant === 'india' ? formatIndianCompactNumber(statValue) : formatNumber(statValue);

    return (
        <TrackImpression
            trackFunc={() => tracker.publishEvent(createDiscoveryImpressionEvent(discoveryEvent))}
        >
            <div className={styles.topic}>
                <Button
                    componentClass="a"
                    udStyle="link"
                    size="large"
                    typography="ud-text-lg ud-text-bold"
                    href={topic.url}
                    onClick={() =>
                        tracker.publishEvent(createDiscoveryItemClickEvent(discoveryEvent))
                    }
                >
                    {topic.title}
                    <Icon glyph="next" label={false} />
                </Button>
                {enrollmentCount != null && (
                    <span
                        className={classNames(
                            'ud-text-sm',
                            styles['topic-learners'],
                            variant !== 'default' && styles['stat-darker'],
                        )}
                    >
                        {ninterpolate(
                            '%(statValue)s learner',
                            '%(statValue)s learners',
                            enrollmentCount,
                            {statValue: formatStatValue(enrollmentCount)},
                        )}
                    </span>
                )}
            </div>
        </TrackImpression>
    );
};

const TopSkillColumn = ({
    topic,
    variant,
}: {
    topic: TrendingTopic & {enrollmentCount: number};
    variant: TrendingSkillsVariant;
}) => {
    const {gettext, interpolate, ninterpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const tracker = useTracker();
    const discoveryEvent = createTrendingSkillsDiscoveryTrackingData(topic, 0);
    const formattedStatValue =
        variant === 'india'
            ? formatIndianCompactNumber(topic.enrollmentCount)
            : formatNumber(topic.enrollmentCount);

    return (
        <TrackImpression
            trackFunc={() => tracker.publishEvent(createDiscoveryImpressionEvent(discoveryEvent))}
        >
            <div className={styles['top-skill']}>
                <SubHeading className="ud-heading-xxl">
                    {variant !== 'default'
                        ? gettext('AI skills for work')
                        : interpolate(
                              gettext('%(topic)s is a top skill'),
                              {topic: topic.title},
                              true,
                          )}
                </SubHeading>
                <Button
                    componentClass="a"
                    udStyle="link"
                    size="large"
                    className={styles['top-skill-cta']}
                    typography="ud-text-lg ud-text-bold"
                    href={topic.url}
                    onClick={() =>
                        tracker.publishEvent(createDiscoveryItemClickEvent(discoveryEvent))
                    }
                >
                    {interpolate(gettext('See %(topic)s courses'), {topic: topic.title}, true)}
                    <Icon glyph="next" label={false} />
                </Button>
                <span
                    className={classNames(
                        'ud-text-sm',
                        styles['top-skill-stat'],
                        variant !== 'default' && styles['stat-darker'],
                    )}
                >
                    {ninterpolate(
                        '%(statValue)s learner',
                        '%(statValue)s learners',
                        topic.enrollmentCount,
                        {statValue: formattedStatValue},
                    )}
                </span>
            </div>
        </TrackImpression>
    );
};

/** Maps to literal `gettext()` calls so extract-locale-keys can pick these up. */
const getCategoryLabel = (categoryName: string, gettext: (s: string) => string): string => {
    switch (categoryName) {
        case 'Development':
            return gettext('Development');
        case 'Design':
            return gettext('Design');
        case 'Business':
            return gettext('Business');
        case 'Data Analytics':
            return gettext('Data Analytics');
        case 'Business skills':
            return gettext('Business skills');
        case 'Career Certifications':
            return gettext('Career Certifications');
        default:
            return categoryName;
    }
};

const CategoryColumns = ({
    categoryTopics,
    enrollmentStats,
    variant,
    excludeTopicId,
}: {
    categoryTopics: CategoryTopics[];
    enrollmentStats: EnrollmentStats;
    variant: TrendingSkillsVariant;
    excludeTopicId: number;
}) => {
    const {gettext} = useI18n();

    return (
        <>
            {categoryTopics.map((category) => (
                <div
                    key={category.categoryId}
                    className={classNames(
                        styles.column,
                        variant === 'japan' && styles['japan-variant'],
                    )}
                    data-purpose="trending-topics-column"
                >
                    <span className="ud-text-xl ud-text-bold">
                        {getCategoryLabel(category.categoryName, gettext)}
                    </span>
                    {category.topics
                        .filter((topic) => topic.id !== excludeTopicId)
                        .slice(0, TOPIC_LIMIT)
                        .map((topic, position) => (
                            <TopicRow
                                key={topic.id}
                                topic={topic}
                                position={position}
                                enrollmentCount={enrollmentStats[String(topic.id)]?.stat_value}
                                variant={variant}
                            />
                        ))}
                </div>
            ))}
        </>
    );
};

const getHeading = (variant: TrendingSkillsVariant, gettext: (s: string) => string) => {
    if (variant === 'japan') return gettext('Trending topics in Japan');
    if (variant === 'india') return gettext('Trending topics in India');
    return gettext('Popular Skills');
};

interface TrendingSkillsHeaderWrapperProps {
    variant?: TrendingSkillsVariant;
    children: React.ReactNode;
}

export const TrendingSkillsHeaderWrapper: React.FC<TrendingSkillsHeaderWrapperProps> = ({
    variant = 'default',
    children,
}) => {
    const {gettext} = useI18n();

    return (
        <OnePaneLayoutFullWidthSection>
            <section className={styles['trending-skills']} data-purpose="trending-skills">
                <div className={styles.container}>
                    <div className={styles['heading-row']}>
                        <Heading className="ud-heading-xxl">{getHeading(variant, gettext)}</Heading>
                    </div>
                    {children}
                </div>
            </section>
        </OnePaneLayoutFullWidthSection>
    );
};

export const TrendingSkills: React.FC<TrendingSkillsProps> = ({variant = 'default'}) => {
    const data = useTrendingSkills(variant);
    const {gettext} = useI18n();
    const tracker = useTracker();

    if (data === null) return null;
    if (data === undefined) return <TrendingSkillsLoadingSkeleton variant={variant} />;

    return (
        <TrendingSkillsHeaderWrapper variant={variant}>
            <div
                className={classNames(
                    styles.content,
                    variant === 'japan' && styles['content-japan-variant'],
                )}
            >
                <div
                    className={classNames(
                        styles['top-skill-with-cta'],
                        variant === 'japan' && styles['top-skill-with-cta-japan-variant'],
                    )}
                >
                    <TopSkillColumn topic={data.topSkill} variant={variant} />
                    <Button
                        componentClass="a"
                        href="/featured-topics/"
                        udStyle="secondary"
                        className={styles['section-cta']}
                        onClick={() =>
                            tracker.publishEvent(
                                createClickEvent({componentName: 'showAllTrendingSkills'}),
                            )
                        }
                    >
                        {variant !== 'default'
                            ? gettext('Show all trending topics')
                            : gettext('Show all trending skills')}
                        <Icon glyph="trending-graph" label={false} />
                    </Button>
                </div>

                <div className={styles['wrapped-trending-topics']}>
                    <CategoryColumns
                        categoryTopics={data.categoryTopics}
                        enrollmentStats={data.enrollmentStats}
                        variant={variant}
                        excludeTopicId={data.topSkill.id}
                    />
                </div>
            </div>
        </TrendingSkillsHeaderWrapper>
    );
};

/** Pre-composed loading state — used both here (data === undefined) and as LazySection's placeholder in lohp-page.tsx. */
export const TrendingSkillsLoadingSkeleton: React.FC<{variant?: TrendingSkillsVariant}> = ({
    variant = 'default',
}) => (
    <TrendingSkillsHeaderWrapper variant={variant}>
        <TrendingSkillsSkeleton />
    </TrendingSkillsHeaderWrapper>
);
